import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { AlertCircle, HelpCircle, X } from 'lucide-react';

interface ModalContextType {
    showAlert: (message: string, title?: string) => void;
    showConfirm: (message: string, onConfirm: () => void, onCancel?: () => void, title?: string) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: { children: ReactNode }) {
    const [alertConfig, setAlertConfig] = useState<{ message: string; title: string; open: boolean } | null>(null);
    const [confirmConfig, setConfirmConfig] = useState<{ message: string; title: string; onConfirm: () => void; onCancel?: () => void; open: boolean } | null>(null);

    const showAlert = (message: string, title: string = 'Notice') => {
        setAlertConfig({ message, title, open: true });
    };

    const showConfirm = (message: string, onConfirm: () => void, onCancel?: () => void, title: string = 'Confirm Action') => {
        setConfirmConfig({ message, title, onConfirm, onCancel, open: true });
    };

    const handleCloseAlert = () => {
        setAlertConfig(prev => prev ? { ...prev, open: false } : null);
        setTimeout(() => setAlertConfig(null), 300); // Wait for transition
    };

    const handleConfirmYes = () => {
        if (confirmConfig?.onConfirm) confirmConfig.onConfirm();
        setConfirmConfig(prev => prev ? { ...prev, open: false } : null);
        setTimeout(() => setConfirmConfig(null), 300);
    };

    const handleConfirmNo = () => {
        if (confirmConfig?.onCancel) confirmConfig.onCancel();
        setConfirmConfig(prev => prev ? { ...prev, open: false } : null);
        setTimeout(() => setConfirmConfig(null), 300);
    };

    return (
        <ModalContext.Provider value={{ showAlert, showConfirm }}>
            {children}

            {/* Alert Modal */}
            {alertConfig && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 9999, padding: 20,
                    opacity: alertConfig.open ? 1 : 0, transition: 'opacity 0.2s ease', pointerEvents: alertConfig.open ? 'auto' : 'none'
                }}>
                    <div className="card animate-in" style={{
                        maxWidth: 400, width: '100%', textAlign: 'center', padding: 32,
                        position: 'relative'
                    }}>
                        <button onClick={handleCloseAlert} style={{
                            position: 'absolute', top: 16, right: 16, background: 'none', border: 'none',
                            color: 'var(--text-muted)', cursor: 'pointer'
                        }}><X size={20} /></button>
                        
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%',
                            background: 'rgba(56, 189, 248, 0.1)', color: 'var(--neon-blue)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
                        }}>
                            <AlertCircle size={28} />
                        </div>
                        <h3 style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.4rem', marginBottom: 12 }}>{alertConfig.title}</h3>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{alertConfig.message}</p>
                        
                        <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleCloseAlert}>Okay</button>
                    </div>
                </div>
            )}

            {/* Confirm Modal */}
            {confirmConfig && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 9999, padding: 20,
                    opacity: confirmConfig.open ? 1 : 0, transition: 'opacity 0.2s ease', pointerEvents: confirmConfig.open ? 'auto' : 'none'
                }}>
                    <div className="card animate-in" style={{
                        maxWidth: 400, width: '100%', textAlign: 'center', padding: 32,
                        position: 'relative'
                    }}>
                         <button onClick={handleConfirmNo} style={{
                            position: 'absolute', top: 16, right: 16, background: 'none', border: 'none',
                            color: 'var(--text-muted)', cursor: 'pointer'
                        }}><X size={20} /></button>
                        
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%',
                            background: 'rgba(245, 158, 11, 0.1)', color: 'var(--warning)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
                        }}>
                            <HelpCircle size={28} />
                        </div>
                        <h3 style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.4rem', marginBottom: 12 }}>{confirmConfig.title}</h3>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{confirmConfig.message}</p>
                        
                        <div style={{ display: 'flex', gap: 12 }}>
                            <button className="btn btn-secondary" style={{ flex: 1 }} onClick={handleConfirmNo}>Cancel</button>
                            <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleConfirmYes}>Confirm</button>
                        </div>
                    </div>
                </div>
            )}
        </ModalContext.Provider>
    );
}

export function useModal() {
    const context = useContext(ModalContext);
    if (context === undefined) {
        throw new Error('useModal must be used within a ModalProvider');
    }
    return context;
}
